import React from 'react';
import {
  AppButton,
  Box,
  Inscription,
  NavigationLeftIcon,
  ProgressBar,
} from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/wrappers';

import { useConfirmUpdatesModal } from './hooks/useConfirmUpdatesModal';
import ConfirmUpdatesGroupCard from './ConfirmUpdatesGroupCard';
import { ConfirmUpdatesModalProps } from './ConfirmUpdatesModal.types';
import {
  BACK_BUTTON_LABEL,
  CONFIRM_BUTTON_LABEL,
  SYNC_UPDATES_STEPS,
  SyncUpdatesStep,
  TABLE_HEADERS,
} from './SyncUpdatesModal.constants';
import styled from 'styled-components';

const OneStepBackButton = styled(Box)`
  position: absolute;
  top: 24px;
  left: 24px;
  cursor: pointer;
`;

const ConfirmUpdatesModal: React.FC<ConfirmUpdatesModalProps> = ({
  visible,
  selectedProperties,
  allChanges,
  onBack,
  onClose,
  onConfirm,
}) => {
  const { groups, selectedProperties: selected, isAppliedToLowerOrgs } = useConfirmUpdatesModal({ selectedProperties, allChanges });

  return (
    <ModalWrapper
      testId="confirm-updates-modal"
      visible={visible}
      onSetVisible={(value) => { if (!value) onClose(); }}
      isClosingByClickOutsideBlocked
      maxWidth="720px"
    >
      {onBack && (
        <OneStepBackButton onClick={onBack} data-testid="back-icon-button">
          <NavigationLeftIcon color="iconPrimary"
            size="18px"
          />
        </OneStepBackButton>
      )}
      <Box direction="column" gap="spacing24">

        <Box align="center">
          <Box width="65%">
            <ProgressBar steps={SYNC_UPDATES_STEPS} activeKey={SyncUpdatesStep.CONFIRM} />
          </Box>
        </Box>

        <Box align="center">
          <Inscription data-testid="confirm-updates-modal_header" size="2xl" color="textTitle" weight="bold" lineHeight="32px" textAlign="center">
            Confirm Update Scope
          </Inscription>
          <Box margin={{ top: 'spacing8' }}>
            <Inscription size="medium" color="textBody" lineHeight="20px" textAlign="center">
              Review the changes for this organization and how they will be applied to lower levels.
            </Inscription>
          </Box>
        </Box>
        <Box round="moduleRound" background="module" direction="column" pad="spacing24">
          <Inscription weight="bold" color="textTitle" margin={{ bottom: 'spacing12' }} data-testid="confirm-updates-modal_updates-count">
            Updates: {selected.length}
          </Inscription>
          <Box direction="column" round="moduleRound" border={{ color: 'border2' }} background="module">
            <Box direction="column" round="moduleRound" background="canvas" pad="spacing24">
              <Box
                direction="row"
                align="center"
                round="container2Round"
                background="legend"
                pad={{ horizontal: 'spacing16' }}
                margin={{ bottom: 'spacing8' }}
                height="32px"
              >
                <Box flex>
                  <Inscription weight="bold" color="textTitle" data-testid="confirm-updates-modal_header-property-name" lineHeight='18px'>
                    {TABLE_HEADERS.propertyName}
                  </Inscription>
                </Box>
                <Box width="80px" align="center">
                  <Inscription weight="bold" color="textTitle" data-testid="confirm-updates-modal_header-this-org" lineHeight='18px'>
                    {TABLE_HEADERS.thisOrg}
                  </Inscription>
                </Box>
                <Box width="120px" align="center">
                  <Inscription weight="bold" color="textTitle" data-testid="confirm-updates-modal_header-lower-orgs" lineHeight='18px'>
                    {TABLE_HEADERS.lowerLevelOrgs}
                  </Inscription>
                </Box>
              </Box>

              {groups.map((group, groupIndex) => (
                <ConfirmUpdatesGroupCard
                  key={group.section}
                  group={group}
                  selected={selected}
                  isAppliedToLowerOrgs={isAppliedToLowerOrgs}
                  showSeparator={groupIndex > 0}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box direction="row" justify="end" gap="spacing12">
          <AppButton
            testId="confirm-updates-modal_back-button-footer"
            buttonType="secondary"
            width="160px"
            style={{ height: '40px' }}
            onClick={onBack}
          >
            {BACK_BUTTON_LABEL}
          </AppButton>
          <AppButton
            testId="confirm-updates-modal_confirm-button"
            width="160px"
            style={{ height: '40px' }}
            onClick={onConfirm}
          >
            {CONFIRM_BUTTON_LABEL}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default ConfirmUpdatesModal;
